import { FormInput, FormLabel, Form, Text, FormFieldGroup, Button, Alert } from '$components'
import { ElementIntent } from '$components'
import * as z from 'zod'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { AuthRoutePath } from '../routes/public'
import { authResetPasswordMutation } from '$api-client/@tanstack/react-query.gen'
import { QueryErrorHelper } from '$utils/query'
import { ToastHelper } from '$components'
import { usePageTitle } from '$components'

enum Input {
  Password = 'password',
  ConfirmPassword = 'confirm_password',
}

const schema = z.object({
  [Input.Password]: z.string().min(8, 'Password must be at least 8 characters').trim(),
  [Input.ConfirmPassword]: z.string().min(1, 'Password confirmation is required').trim(),
}).superRefine(({ password, confirm_password: confirmPassword }, ctx) => {
  if (password !== confirmPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Passwords do not match',
      path: [Input.ConfirmPassword],
    })
  }
})

type FormValues = z.infer<typeof schema>

export function ResetPasswordPage() {
  usePageTitle('Reset Password')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  const { mutate: resetPassword, isPending, error } = useMutation({
    ...authResetPasswordMutation(),
    onSuccess: () => navigate(AuthRoutePath.ResetPasswordConfirmation, { replace: true }),
  })

  const onSubmit = (values: FormValues) => {
    if (!token || !email) {
      return ToastHelper.error('Failed to reset password. Please try again.')
    }

    resetPassword({
      body: {
        email,
        token,
        password: values.password,
        confirm_password: values.confirm_password,
      },
    })
  }

  return (
    <div className="flex items-center justify-center h-screen w-full">
      <div className="w-full max-w-prose-sm flex flex-col gap-stack-section">
        <div className="space-y-stack-tight">
          <Text type="h1">
            Reset Password
          </Text>

          <Text className="text-fg-subtle">
            Choose your new password.
          </Text>
        </div>

        <Form
          onSubmit={onSubmit}
          schema={schema}
        >
          {() => (
            <div className="flex flex-col gap-stack-roomy">
              <FormFieldGroup>
                <FormLabel htmlFor={Input.Password}>
                  New Password
                </FormLabel>

                <FormInput
                  name={Input.Password}
                  type="password"
                  placeholder="Enter your new password"
                />
              </FormFieldGroup>

              <FormFieldGroup>
                <FormLabel htmlFor={Input.ConfirmPassword}>
                  Confirm New Password
                </FormLabel>

                <FormInput
                  name={Input.ConfirmPassword}
                  type="password"
                  placeholder="Confirm your new password"
                />
              </FormFieldGroup>

              <Button
                type="submit"
                disabled={isPending}
                isLoading={isPending}
                className="w-full mt-stack-roomy"
                size="lg"
              >
                Reset password
              </Button>

              { error && (
                <Alert intent={ElementIntent.Danger}>
                  <Alert.Heading>
                    Something went wrong!
                  </Alert.Heading>

                  <Alert.Body>
                    { QueryErrorHelper.getQueryError(error).message }
                  </Alert.Body>
                </Alert>
              )}
            </div>
          )}
        </Form>
      </div>
    </div>
  )
}
